import React, { useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import mainAxios from "../../../services/mainAxios";
import Loader from "../../Layout/Loader";
import DatePicker from "react-datepicker";
import moment from "moment";

const GenerateWtnIcModal = ({ user, selectedCompany, onSuccess }) => {
  const { t } = useTranslation("translations");
  const [loading, setLoading] = useState(false);

  const softCode = user.companiesRights?.[0]?.branches?.[0]?.tcrTypes?.[0]?.softCode || "";
  const company = user.companiesRights.find(
    (company) => company.id === parseInt(selectedCompany)
  );

  const defaultParams = {
    issuerNuis: "",
    dateTimeCreated: "",
    invoiceNumber: "",
    businessUnitCode: "",
    softCode: "",
  };

  const [errors, setErrors] = useState(defaultParams);
  const [wtnic, setWtnic] = useState(null);

  const [params, setParams] = useState({
    ...defaultParams,
    issuerNuis: company?.nuis || "",
    softCode,
  });

  const handleChange = (e, selectKey, selectValue) => {
    const key = e ? e?.target?.name : selectKey;
    let value = e ? e?.target?.value : selectValue;
    if (errors[key]) {
      setErrors((prevState) => ({ ...prevState, [key]: null }));
    }
    setParams({ ...params, [key]: value });
  };

  const validateCode = (code) => {
    return new RegExp("[a-z]{2}[0-9]{3}[a-z]{2}[0-9]{3}").test(code);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const {
      issuerNuis,
      dateTimeCreated,
      invoiceNumber,
      businessUnitCode,
      softCode,
    } = params;

    let checkNiptFormat = new RegExp("[a-zA-Z]{1}[0-9]{8}[a-zA-Z]{1}").test(issuerNuis);

    let checkError =
      !issuerNuis ||
      !dateTimeCreated ||
      !invoiceNumber ||
      !businessUnitCode ||
      !validateCode(businessUnitCode) ||
      !softCode ||
      !checkNiptFormat;

    if (!checkNiptFormat || !issuerNuis) {
      setErrors((errors) => ({ ...errors, issuerNuis: "toast.incorrectNiptForCustomer" }));
    }
    if (!validateCode(softCode) || !softCode) {
      setErrors((errors) => ({ ...errors, softCode: "nslf.softCodeError" }));
    }
    if (!dateTimeCreated) {
      setErrors((errors) => ({ ...errors, dateTimeCreated: "nslf.dateTimeCreatedError" }));
    }
    if (!invoiceNumber) {
      setErrors((errors) => ({ ...errors, invoiceNumber: "nslf.invoiceNumberError" }));
    }
    if (!validateCode(businessUnitCode) || !businessUnitCode) {
      setErrors((errors) => ({ ...errors, businessUnitCode: "nslf.businessUnitCodeError" }));
    }

    if (checkError) {
      setLoading(false);
      return;
    }

    setLoading(true);
    let parsedDate = moment(params.dateTimeCreated).format();

    mainAxios
      .post("/apiEndpoint/generateNSLFAndWTNIC", {
        object: "WTNIC",
        params: { ...params, dateTimeCreated: parsedDate },
        nuis: user.companiesRights.find(
          (data) => data.id === parseInt(selectedCompany)
        )?.nuis,
        company: selectedCompany,
        type: "basic",
      })
      .then((res) => {
        if (res?.data?.status === true) {
          setWtnic(res.data.content[0]);
          toast.success(t(["toast.nslfGenerated"]), { containerId: "all_toast" });
          if (onSuccess) onSuccess();
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="row">
        <div className="col-md-6">
          <div className="form-group">
            <label>{t("nslf.issuerNuis")}<span className="text-danger"> *</span></label>
            <input
              type="text"
              className={`form-control ${errors.issuerNuis ? "is-invalid" : ""}`}
              name="issuerNuis"
              value={params.issuerNuis}
              onChange={handleChange}
            />
            {errors.issuerNuis && <div className="invalid-feedback">{t(errors.issuerNuis)}</div>}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-group">
            <label>{t("nslf.dateTimeCreated")}<span className="text-danger"> *</span></label>
            <DatePicker
              selected={params.dateTimeCreated}
              dateFormat="dd/MM/yyyy HH:mm:ss"
              name="dateTimeCreated"
              showTimeSelect
              onChange={(date) => handleChange(null, "dateTimeCreated", date)}
              customInput={
                <input className={`form-control ${errors.dateTimeCreated ? "is-invalid" : ""}`} />
              }
            />
            {errors.dateTimeCreated && <div className="invalid-feedback d-block">{t(errors.dateTimeCreated)}</div>}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-group">
            <label>{t("nslf.invoiceNumber")}<span className="text-danger"> *</span></label>
            <input
              type="number"
              className={`form-control ${errors.invoiceNumber ? "is-invalid" : ""}`}
              name="invoiceNumber"
              value={params.invoiceNumber}
              onChange={handleChange}
            />
            {errors.invoiceNumber && <div className="invalid-feedback">{t(errors.invoiceNumber)}</div>}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-group">
            <label>{t("nslf.businessUnitCode")}<span className="text-danger"> *</span></label>
            <input
              type="text"
              className={`form-control ${errors.businessUnitCode ? "is-invalid" : ""}`}
              name="businessUnitCode"
              value={params.businessUnitCode}
              onChange={handleChange}
            />
            {errors.businessUnitCode && <div className="invalid-feedback">{t(errors.businessUnitCode)}</div>}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-group">
            <label>{t("nslf.softCode")}<span className="text-danger"> *</span></label>
            <input
              type="text"
              className={`form-control ${errors.softCode ? "is-invalid" : ""}`}
              name="softCode"
              value={params.softCode}
              onChange={handleChange}
            />
            {errors.softCode && <div className="invalid-feedback">{t(errors.softCode)}</div>}
          </div>
        </div>
        {wtnic && (
          <div className="col-md-6">
            <div className="form-group">
              <label>WTN IC</label>
              <input
                type="text"
                className="form-control"
                value={wtnic}
                disabled
                readOnly
              />
            </div>
          </div>
        )}
      </div>
      <div className="mt-3">
        <button className="btn btn-primary" type="submit" disabled={loading}>
          {loading ? (
            <Loader size="xs" color="white" />
          ) : (
            <>
              <FontAwesomeIcon icon={faCheck} className="mr-2" />
              {t("navbar.create")}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default GenerateWtnIcModal;
